/**
 * Cloud sign-in screen. Shown by the cloud shell whenever
 * `useAuth().status === 'unauthenticated'`.
 *
 * Passwordless by design: the Workers runtime can't run our argon2 build,
 * so cloud auth is a one-shot emailed link (which also creates the account
 * for a new email — see the API's magic-link verify) or OAuth. Password
 * routes still exist API-side for self-hosted Node deployments.
 */

import { useEffect, useState } from 'react';

import { authErrorCode } from '../router.js';

import { BliksysMark } from './BliksysMark.js';
import './SignIn.css';

/** Friendly copy for the `auth_error` codes the API bounces back. */
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  'link-missing': 'That sign-in link was incomplete. Request a new one below.',
  'link-invalid': 'That sign-in link is invalid or has expired. Request a new one below.',
  'oauth-unknown-provider': "That sign-in method isn't available. Try an emailed link instead.",
  'oauth-not-configured': "That sign-in method isn't available here. Try an emailed link instead.",
  'oauth-failed': 'Signing in with that provider failed. Please try again.',
  'server-error': 'Something went wrong signing you in. Please try again.',
};

function authErrorMessage(): string | null {
  const code = authErrorCode();
  return code ? (AUTH_ERROR_MESSAGES[code] ?? 'Sign-in failed. Please try again.') : null;
}

/** Drop the `auth_error` param from the URL so a refresh doesn't re-show it.
 * Idempotent — safe under StrictMode's double-invoked effects. */
function clearAuthErrorParam(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('auth_error')) return;
  url.searchParams.delete('auth_error');
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
}

interface SignInProps {
  /** Pre-fill the email (e.g. an invite recipient). */
  initialEmail?: string;
  /** Override the card title/subtitle (e.g. on the invite page). */
  heading?: string;
  subheading?: string;
}

export function SignIn({ initialEmail = '', heading, subheading }: SignInProps = {}): JSX.Element {
  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  // A bounce-back from a failed magic-link / OAuth attempt, surfaced as a
  // banner so the user can immediately retry here. Read once from the URL
  // (lazy init), then strip the param in an effect — keeping the two apart
  // means StrictMode's double-invoked effect can't clear the message.
  const [notice] = useState<string | null>(authErrorMessage);
  useEffect(() => {
    clearAuthErrorParam();
  }, []);

  const apiBase = import.meta.env.BLIKSYS_API_URL ?? 'http://localhost:3001';

  async function handleMagicLinkSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await fetch(`${apiBase}/v1/auth/magic-link/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        // Return to where we are after verifying — preserves an invite, and
        // keeps cloud mode in dev (where the bare origin is free mode).
        body: JSON.stringify({
          email,
          returnTo: window.location.pathname + window.location.search,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? `Request failed (${String(res.status)}).`);
      }
      setMagicLinkSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send link.');
    } finally {
      setBusy(false);
    }
  }

  const oauthHref = (provider: 'google' | 'github'): string =>
    `${apiBase}/v1/auth/oauth/${provider}/start?return_to=${encodeURIComponent(window.location.href)}`;

  return (
    <div className="signin">
      <div className="signin-card">
        <div className="signin-logo" aria-hidden="true">
          <BliksysMark size={40} />
        </div>
        <h1 className="signin-title">{heading ?? 'Bliksys Cloud'}</h1>
        <p className="signin-subtitle">
          {subheading ??
            "Enter your email and we'll send a one-time sign-in link. New here? The same link creates your account."}
        </p>

        {notice && (
          <p className="signin-notice" role="alert">
            {notice}
          </p>
        )}

        {magicLinkSent ? (
          <div className="signin-magic-confirm" role="status">
            <p>
              A sign-in link is on its way to <strong>{email}</strong>. Check your inbox — the link
              expires in 15 minutes and signs you in (or creates your account if you&apos;re new).
            </p>
            <button
              type="button"
              className="signin-submit"
              onClick={() => {
                setMagicLinkSent(false);
                setEmail('');
              }}
            >
              Send another
            </button>
          </div>
        ) : (
          <form className="signin-form" onSubmit={(e) => void handleMagicLinkSubmit(e)}>
            <label className="signin-field">
              <span className="signin-label">Email</span>
              <input
                type="email"
                required
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </label>
            {error && (
              <p className="signin-error" role="alert">
                {error}
              </p>
            )}
            <button type="submit" className="signin-submit" disabled={busy}>
              {busy ? 'Sending…' : 'Email me a sign-in link'}
            </button>
          </form>
        )}

        <div className="signin-divider" role="presentation">
          <span>or</span>
        </div>

        <div className="signin-oauth">
          <a className="signin-oauth-button" href={oauthHref('google')}>
            Continue with Google
          </a>
          <a className="signin-oauth-button" href={oauthHref('github')}>
            Continue with GitHub
          </a>
        </div>
      </div>
    </div>
  );
}
